"use client"

/**
 * @fileoverview In-app browser hint for checkout and auth flows.
 * @description Social in-app WebViews (+275% Safari in-app in GA) often break
 * cookies, referrers, and magic-link handoff. This nudges users to open in Safari/Chrome.
 *
 * @module components/shared/InAppBrowserHint
 */

import { useEffect, useState, type ReactNode } from "react"
import {
  detectInAppBrowser,
  inAppBrowserDisplayName,
} from "~/lib/analytics/in-app-browser"

interface InAppBrowserHintProps {
  /** Checkout vs auth copy variant. */
  context: "checkout" | "auth"
  /** Optional extra class names. */
  className?: string
}

/**
 * Shows a dismissible hint when the page runs inside a social in-app browser.
 *
 * @param {InAppBrowserHintProps} props - Component props.
 * @returns {ReactNode} Hint banner or null.
 */
export function InAppBrowserHint({ context, className }: InAppBrowserHintProps): ReactNode {
  const [visible, setVisible] = useState(false)
  const [appLabel, setAppLabel] = useState("this app")

  useEffect(() => {
    const info = detectInAppBrowser()
    if (!info.isInApp) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reflects client-only WebView detection on mount (unavailable during SSR)
    setAppLabel(inAppBrowserDisplayName(info.app))
    setVisible(true)
  }, [])

  if (!visible) return null

  const action =
    context === "checkout"
      ? "Checkout and license delivery work best in your default browser."
      : "Magic-link sign-in works best in your default browser."

  const copyUrl = (): void => {
    if (typeof window === "undefined") return
    void navigator.clipboard?.writeText(window.location.href)
  }

  return (
    <div
      className={`rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-950 dark:text-amber-100 ${className ?? ""}`}
      role="status"
    >
      <p className="font-medium">Opened from {appLabel}?</p>
      <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
        {action} Tap the menu (⋯) and choose <strong>Open in Browser</strong> (Safari or Chrome).
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
          onClick={copyUrl}
        >
          Copy page link
        </button>
        <button
          type="button"
          className="rounded-md px-3 py-1.5 text-xs font-medium text-amber-900 underline dark:text-amber-100"
          onClick={() => setVisible(false)}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}
